import React, { useState, useRef, useCallback, useEffect } from 'react';
import Sidebar from './Sidebar';
import { NeuroaLogo } from '../components/ui/NeuroaLogo';
import { NeuroaBridgeProvider, useNeuroaBridge } from '../context/NeuroaBridgeContext';
import NeuroaSidePanel from '../components/Neuroa/NeuroaSidePanel';
import NeuroaTooltip from '../components/Neuroa/NeuroaTooltip';
import { cn } from '../utils/cn';
import logo from '../assets/images/SATI/IBi-logo.png';

export function DashboardLayout({ children }) {
    const [isSidebarOpen, setIsSidebarOpenLocal] = useState(true);
    const [isNeuroaOpen, setIsNeuroaOpen] = useState(false);
    
    // Read the initial width from CSS variables to have a single source of truth
    const getInitialWidth = () => {
        const rootStyle = getComputedStyle(document.documentElement);
        const width = rootStyle.getPropertyValue('--neuroa-sp-width').trim();
        return parseInt(width) || 450;
    };

    const [neuroaWidth, setNeuroaWidth] = useState(getInitialWidth);
    const [isResizing, setIsResizing] = useState(false);
    const neuroaLogoRef = useRef(null);

    // Ensure the CSS variable is initialized and synced with state
    useEffect(() => {
        document.documentElement.style.setProperty('--neuroa-sp-width', `${neuroaWidth}px`);
    }, [neuroaWidth]);

    const toggleMaximize = useCallback(() => {
        setNeuroaWidth(prev => {
            const isCurrentlyMaximized = prev > (window.innerWidth - 100);
            return isCurrentlyMaximized ? 450 : window.innerWidth;
        });
    }, []);

    const openNeuroa = useCallback(() => {
        neuroaLogoRef.current && neuroaLogoRef.current.wink();
        setIsSidebarOpenLocal(false);
        setIsNeuroaOpen(true);
    }, []);

    const closeNeuroa = useCallback(() => {
        setIsNeuroaOpen(false);
        setIsSidebarOpenLocal(true);
    }, []);

    const startResizing = useCallback((mouseDownEvent) => {
        mouseDownEvent.preventDefault();
        setIsResizing(true);
    }, []);

    const stopResizing = useCallback(() => {
        setIsResizing(false);
        // Sync the state with the actual CSS variable value when we stop
        const root = document.documentElement;
        const currentWidth = parseInt(root.style.getPropertyValue('--neuroa-sp-width')) || neuroaWidth;
        setNeuroaWidth(currentWidth);
    }, [neuroaWidth]);

    const resize = useCallback((mouseMoveEvent) => {
        if (isResizing) {
            const newWidth = mouseMoveEvent.clientX;
            if (newWidth > 300 && newWidth < 1200) { // Min 300px, Max 1200px
                // Update CSS variable directly for smooth resizing without re-renders
                document.documentElement.style.setProperty('--neuroa-sp-width', `${newWidth}px`);
            }
        }
    }, [isResizing]);

    useEffect(() => {
        window.addEventListener("mousemove", resize);
        window.addEventListener("mouseup", stopResizing);
        return () => {
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResizing);
        };
    }, [resize, stopResizing]);

    return (
        <NeuroaBridgeProvider onOpenNeuroa={openNeuroa}>
            <DashboardLayoutContent 
                isSidebarOpen={isSidebarOpen} 
                setIsSidebarOpenLocal={setIsSidebarOpenLocal}
                isNeuroaOpen={isNeuroaOpen}
                setIsNeuroaOpen={setIsNeuroaOpen}
                closeNeuroa={closeNeuroa}
                neuroaLogoRef={neuroaLogoRef}
                neuroaWidth={neuroaWidth}
                startResizing={startResizing}
                isResizing={isResizing}
                toggleMaximize={toggleMaximize}
            >
                {children}
            </DashboardLayoutContent>
        </NeuroaBridgeProvider>
    );
}

function DashboardLayoutContent({ children, isSidebarOpen, setIsSidebarOpenLocal, isNeuroaOpen, setIsNeuroaOpen, closeNeuroa, neuroaLogoRef, neuroaWidth, startResizing, isResizing, toggleMaximize }) {
    const { 
        setIsSidebarOpen: setBridgeSidebarOpen, 
        setIsNeuroaOpen: setBridgeNeuroaOpen, 
        openNeuroa: triggerOpenNeuroa,
        showNeuroaWelcome,
        neuroaMessage
    } = useNeuroaBridge();

    // Sync state only when it changes from external sources, 
    // but the handlers now handle the bulk of the work.
    useEffect(() => {
        setBridgeSidebarOpen(isSidebarOpen);
    }, [isSidebarOpen, setBridgeSidebarOpen]);

    useEffect(() => {
        setBridgeNeuroaOpen(isNeuroaOpen);
    }, [isNeuroaOpen, setBridgeNeuroaOpen]);

    useEffect(() => {
        // Wink the top-left logo when sidebar toggles and it's visible
        if (!isSidebarOpen && !isNeuroaOpen && neuroaLogoRef.current) {
            neuroaLogoRef.current.wink();
        }
    }, [isSidebarOpen, isNeuroaOpen, neuroaLogoRef]);

    return (
        <div className={cn("min-h-screen bg-gray-100 font-sans relative overflow-x-hidden p-3 md:p-6 flex")}>
            {/* 1. Dynamic Spacer: This element creates the space for the sidebar/Neuroa panel */}
            <div 
                className={cn(
                    "hidden md:block transition-[width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-[width] flex-shrink-0",
                    isResizing && "transition-none"
                )}
                style={{ 
                    width: isNeuroaOpen 
                        ? (neuroaWidth > window.innerWidth - 100 ? '0px' : `${neuroaWidth + 24}px`)
                        : (isSidebarOpen ? '288px' : '96px')
                }}
            />

            {/* Sidebar Desktop - Kept outside the flex flow but anchored to screen */}
            <div className={cn(
                "hidden md:block fixed left-6 top-6 bottom-6 z-[60] transition-[transform,opacity] duration-300 origin-top-left ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-transform",
                isSidebarOpen ? "scale-100 opacity-100 pointer-events-auto" : "scale-0 opacity-0 pointer-events-none"
            )}>
                <Sidebar onClose={() => setIsSidebarOpenLocal(false)} />
            </div>

            {/* Main Content Area */}
            <div className={cn(
                "flex-1 min-w-0 transition-opacity duration-300",
                isNeuroaOpen && neuroaWidth > window.innerWidth - 100 ? "opacity-0 pointer-events-none" : "opacity-100"
            )}>
                {!isSidebarOpen && !isNeuroaOpen && (
                    <div className="hidden md:flex fixed left-6 top-6 z-40 flex-col gap-4">
                        <button 
                            onClick={() => setIsSidebarOpenLocal(true)} 
                            className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-200 group cursor-pointer overflow-hidden"
                        >
                            <img src={logo} alt="Open Sidebar" className="w-8 h-8 object-contain" />
                        </button>
                        
                        <NeuroaTooltip 
                            text={neuroaMessage}
                            forceShow={showNeuroaWelcome}
                            position="right"
                        >
                            <button 
                                onClick={triggerOpenNeuroa} 
                                onMouseEnter={() => neuroaLogoRef.current?.setHover(true)}
                                onMouseLeave={() => neuroaLogoRef.current?.setHover(false)}
                                className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-200 group cursor-pointer overflow-hidden"
                            >
                                <NeuroaLogo ref={neuroaLogoRef} className="w-8 h-8" />
                            </button>
                        </NeuroaTooltip>
                    </div>
                )}

                <div className="min-h-[calc(100vh-3rem)]">
                    {children}
                </div>
            </div>

            {/* Neuroa Panel */}
            <NeuroaSidePanel 
                sessiontoken={sessionStorage.getItem("satiAuth")}
                theme="light"
                isOpen={isNeuroaOpen}
                onClose={closeNeuroa}
                onMaximize={toggleMaximize}
                width={neuroaWidth}
                onMouseDown={startResizing}
                isResizing={isResizing}
            />
            {/* Resizing Overlay (to prevent iframe interaction while dragging) */}
            {isResizing && (
                <div className="fixed inset-0 z-[100] cursor-col-resize bg-transparent" />
            )}
        </div>
    );
}
